// Catalogus van de statische beeldbestanden (docs/DESIGN.md §7). De bestanden zelf staan in
// packages/ui-core/assets en worden door scripts/sync-art.mjs naar apps/{kiosk,web}/public/art
// gekopieerd; componenten verwijzen er met <img src> / <use href> naar en nemen ze NOOIT op in de
// JS-bundle (kiosk-budget, ONTWERP.md §12.2). Alleen id's staan hier.

/** Categorieën voor de IconPicker en de galerij; volgorde = volgorde in de picker. */
export type PictoCategory =
  | 'routine'
  | 'chores'
  | 'school'
  | 'sport'
  | 'hobby'
  | 'care'
  | 'family'
  | 'food'
  | 'groceries'
  | 'reward'
  | 'avatar'
  | 'packing';

export interface PictoGroup {
  id: PictoCategory;
  ids: ReadonlyArray<string>;
}

export const PICTO_GROUPS: ReadonlyArray<PictoGroup> = [
  {
    id: 'routine',
    ids: [
      'brush-teeth',
      'get-dressed',
      'shoes',
      'coat',
      'wash-hands',
      'toilet',
      'shower',
      'bath',
      'sleep',
      'alarm',
      // Bidden (dagelijkse routine, ook als afspraak): geknielde persoon met geheven handen.
      'prayer',
      // Wudu doen (golf 2, oprichter #15): water uit een kraan in twee handen. Eigen tekening.
      'wudu',
    ],
  },
  {
    id: 'chores',
    ids: [
      'make-bed',
      'tidy-up',
      'set-table',
      'clear-table',
      'dishes',
      'dishwasher',
      'vacuum',
      'sweep',
      'cleaning',
      'windows',
      'laundry',
      'fold-laundry',
      'trash',
      'recycling',
      'water-plants',
      'garden',
      'walk-dog',
      'feed-cat',
      'feed-fish',
      'feed-rabbit',
      'mail',
    ],
  },
  {
    id: 'school',
    ids: [
      'school',
      'backpack',
      'homework',
      'reading',
      'library',
      // Lezing (oprichter 1 okt): spreker achter een lessenaar met een scherm. Eigen tekening.
      'lecture',
      // Moskeeles (koranles, Arabische les): een les zoals muziekles, maar in de moskee.
      'mosque',
      // Koranles (0024): open boek op een boekensteun. Opt-in: alleen zichtbaar bij geloof Islam.
      'quran',
      'computer',
      'work',
      'phone-call',
      'calendar',
    ],
  },
  {
    id: 'sport',
    ids: [
      'soccer',
      'hockey',
      'swimming',
      'gymnastics',
      'dance',
      'ballet',
      'tennis',
      'basketball',
      'judo',
      'horse-riding',
      'skating',
      'running',
      'cycling',
      'playground',
    ],
  },
  {
    id: 'hobby',
    ids: [
      'music-lesson',
      'piano',
      'guitar',
      'violin',
      'drums',
      'singing',
      'theater',
      'drawing',
      'painting',
      'crafts',
      'puzzle',
      'play',
      'gaming',
      'tv',
      'screen-time',
    ],
  },
  { id: 'care', ids: ['dentist', 'doctor', 'hospital', 'medicine', 'sick', 'haircut'] },
  {
    id: 'family',
    ids: [
      'birthday',
      'party',
      'gift',
      'wish',
      'chat',
      // Sohbet (oprichter 1 okt): drie mensen rustig in gesprek rond een tafeltje met thee, zonder
      // geloofssymbolen. Alleen bij Turks én Islam in het gezinsprofiel (domain profile/catalog.ts).
      'sohbet',
      'grandma',
      'grandpa',
      'family',
      'babysitter',
      'playdate',
      'sleepover',
      'home',
      'holiday',
      'travel',
      'camping',
      'zoo',
      'museum',
      'cinema',
      'car',
      'bus',
      'train',
      'walk',
      // Geloof (0024, opt-in via packages/domain profile/catalog.ts PICTO_FAITH_TAGS): kerk,
      // sjabbatkaarsen, synagoge, mandir. Pas zichtbaar als het gezin dat geloof koos.
      'church',
      'shabbat',
      'synagogue',
      'mandir',
      // Maansikkel (bijzondere dagen: Ramadan, Mawlid; ook "avond").
      'moon',
    ],
  },
  {
    id: 'food',
    ids: [
      'breakfast',
      'lunch',
      'dinner',
      'fruit',
      'drink',
      'cooking',
      'baking',
      'groceries',
      'ice-cream',
      'popcorn',
      'pizza',
      'pancakes',
      'soup',
      'oven-dish',
      'stamppot',
      'lasagne',
      'curry',
      'noodles',
      'taco',
      'wrap',
      'salad',
      'hamburger',
      'fries',
      'dumplings',
      'waffle',
      'pie',
      'cake',
      'pudding',
    ],
  },
  // Boodschappen (sprint 4, huishouden): producten voor de lijst, voorraad en ingrediënten.
  {
    id: 'groceries',
    ids: [
      'banana',
      'pear',
      'orange',
      'lemon',
      'strawberry',
      'grapes',
      'tomato',
      'cucumber',
      'carrot',
      'broccoli',
      'lettuce',
      'bell-pepper',
      'onion',
      'garlic',
      'potato',
      'mushroom',
      'corn',
      'avocado',
      'cheese',
      'butter',
      'egg',
      'yoghurt',
      'croissant',
      'honey',
      'meat',
      'chicken',
      'bacon',
      'fish',
      'shrimp',
      'sausage',
      'rice',
      'salt',
      'canned-food',
      'peanuts',
      'chocolate',
      'cookie',
      'jar',
      'coffee',
      'tea',
      'juice',
      'water',
      'toilet-paper',
      'soap',
      'sponge',
      'basket',
      'baby-bottle',
      'receipt',
      'store',
    ],
  },
  { id: 'reward', ids: ['star', 'trophy', 'medal', 'savings'] },
  // 0031 (golf 2): wat er mee moet op de checklist bij een oproep ("Tas klaarzetten"). Eigen
  // tekeningen in OpenMoji-stijl; kinderen die nog niet lezen herkennen het ding aan het plaatje.
  {
    id: 'packing',
    ids: [
      'swim-trunks',
      'swimsuit',
      'towel',
      'underwear',
      'socks',
      'shampoo',
      'slippers',
      'football-boots',
      'shin-guards',
      'water-bottle',
      'headscarf',
      'prayer-cap',
    ],
  },
  {
    id: 'avatar',
    ids: [
      'fox',
      'cat',
      'rabbit',
      'bear',
      'panda',
      'koala',
      'owl',
      'lion',
      'tiger',
      'monkey',
      'unicorn',
      'dino',
      'turtle',
      'frog',
      'hedgehog',
      'penguin',
      'dolphin',
      'butterfly',
      'rocket',
      'sunflower',
      'rainbow',
      // Thema's (feat/themas): rustige, abstracte vormen voor volwassenen, naast de dieren.
      'shape-arch',
      'shape-wave',
      'shape-leaf',
      'shape-hills',
      'shape-ring',
      'shape-spark',
    ],
  },
];

const PICTO_SET: Record<string, true> = {};
for (const group of PICTO_GROUPS) for (const id of group.ids) PICTO_SET[id] = true;

/** Alle pictogram-id's (activiteiten, klusjes, avatars). */
export const PICTO_IDS: ReadonlyArray<string> = Object.keys(PICTO_SET);

/**
 * Sprint-1-id's uit de inline set (kiosk Pictogram) die een ander statisch pictogram krijgen.
 * Id's die in beide sets bestaan (school, home, car, star, bath, trash, doctor, party, family,
 * calendar) hoeven hier niet.
 */
export const LEGACY_PICTO_ALIAS: Readonly<Record<string, string>> = {
  sport: 'soccer',
  music: 'music-lesson',
  food: 'dinner',
  bed: 'sleep',
  toys: 'play',
  pets: 'walk-dog',
  book: 'reading',
  clean: 'cleaning',
  shop: 'groceries',
  // Id's uit de ingebouwde sjablonen van sprint 2/3 (migraties 0008/0009) die hier anders heten.
  swim: 'swimming',
  teeth: 'brush-teeth',
  shirt: 'get-dressed',
  plant: 'water-plants',
};

/** Statisch pictogram-id voor een opgeslagen `icon`-waarde, of null. */
export function resolvePicto(icon: string | null | undefined): string | null {
  if (!icon) return null;
  if (PICTO_SET[icon]) return icon;
  const alias = LEGACY_PICTO_ALIAS[icon];
  return alias !== undefined ? alias : null;
}

export const ILLUSTRATION_IDS = [
  'welcome',
  'empty-day',
  'all-done',
  'no-tasks',
  'pair-tablet',
  'offline',
  'night',
  'rewards',
  'invite',
  'oops',
  // Sprint 4 (huishouden): boodschappen, weekmenu, recepten/voorraad.
  'groceries',
  'menu',
  'cooking',
  // w4 familie (scripts/illustrations-family.mjs, docs/DESIGN.md §8.F)
  'wishes',
  'messages',
  'family-link',
  'birthday',
  'holiday',
  'calendar-link',
  // Sprint 4 (kamers): kamers, schoonmaak, kindslot.
  'rooms',
  'cleaning',
  'kid-lock',
  // 0026 weer: lege staat van Weer (scripts/weather-art.mjs).
  'weather',
  // 0023 gezinsprofiel "Wat past bij ons gezin" (scripts/illustrations-profile.mjs, §8.P).
  'family-table',
  // 0032 (golf 3): Groei en School (scripts/illustrations-groei-school.mjs, §8.G en §8.S).
  'growth',
  'school',
  // 0063 (module Hindoeïsme): feestplaten Divali en Phagwa (scripts/illustrations-hindoeisme.mjs).
  'diya',
  'phagwa',
] as const;
export type IllustrationId = (typeof ILLUSTRATION_IDS)[number];

/** Illustraties zijn 4:3 (320 × 240); zet altijd width én height op de <img>. */
export const ILLUSTRATION_RATIO = 240 / 320;

let base = '/art/';

/** Basis-URL van de beeldbestanden (standaard `/art/`, zelfde origin; nooit een CDN, §12.2). */
export function setArtBase(next: string): void {
  base = next.charAt(next.length - 1) === '/' ? next : `${next}/`;
}

export function artBase(): string {
  return base;
}

/**
 * Welke pictogrammenset (feat/themas, docs/ux/themas.md): 'color' = de kleurrijke set (thema
 * Kinderen, assets/picto, als <img>), 'clean' = de rustige lijnset (thema Volwassen,
 * assets/picto-clean, via <use> zodat hij currentColor en het thema volgt). Dezelfde id's.
 */
export type PictoSet = 'color' | 'clean';

/** URL van het kleurpictogram (set 'color'). */
export function pictoUrl(id: string): string {
  return `${base}picto/${id}.svg`;
}

/** URL van de rustige tekening; de groep met id p is het pictogram (`<use href="…#p">`). */
export function cleanPictoUrl(id: string): string {
  return `${base}picto-clean/${id}.svg#p`;
}

// De actieve set. Het bord zet hem per thema (en per scherm), de ouderapp volgt "Mijn thema".
// Een kleine lijst luisteraars in plaats van een context: ook lagen die buiten de boom van het
// bord gerenderd worden (oproepen, bladen) wisselen dan mee.
let activeSet: PictoSet = 'color';
const listeners: Array<() => void> = [];

/** De set die nu geldt. */
export function pictoSet(): PictoSet {
  return activeSet;
}

/** Een andere set kiezen; alle getoonde pictogrammen tekenen opnieuw. */
export function setPictoSet(next: PictoSet): void {
  if (next === activeSet) return;
  activeSet = next;
  for (const l of listeners.slice()) l();
}

/** Luisteren naar een wissel van set (geeft een opruimfunctie terug). */
export function onPictoSet(listener: () => void): () => void {
  listeners.push(listener);
  return () => {
    const i = listeners.indexOf(listener);
    if (i >= 0) listeners.splice(i, 1);
  };
}

export function illustrationUrl(id: IllustrationId): string {
  return `${base}illus/${id}.svg`;
}

export function iconSpriteUrl(): string {
  return `${base}icons.svg`;
}
